import React, { useEffect, useState } from 'react';
import { UGCMusicWaveform } from './UGCMusicWaveform';

type Props = {
  /** HTTPS audio URL (same as assembly / Creatomate). */
  src: string;
  barCount?: number;
  className?: string;
  barClassName?: string;
};

/**
 * Renders peak levels from the audio file when fetch + decode succeed (CORS permitting).
 * Falls back to {@link UGCMusicWaveform} if decode fails or is still loading.
 */
export const UGCAudioWaveform: React.FC<Props> = ({
  src,
  barCount = 48,
  className = '',
  barClassName = 'bg-emerald-100',
}) => {
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setUseFallback(false);
    setPeaks(null);

    const trimmed = src.trim();
    if (!trimmed) {
      setUseFallback(true);
      return;
    }
    try {
      const parsed = new URL(trimmed);
      // SoundHelix demo tracks do not allow cross-origin fetch from localhost.
      // Skip fetch/decode and render fallback bars to avoid noisy CORS errors.
      if (parsed.hostname === 'www.soundhelix.com') {
        setUseFallback(true);
        return;
      }
    } catch {
      // Keep existing fallback behavior for invalid or non-standard URLs.
    }

    const ACtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!ACtx) {
      setUseFallback(true);
      return;
    }

    const ctx = new ACtx();

    const run = async () => {
      try {
        const res = await fetch(trimmed, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arrayBuffer = await res.arrayBuffer();
        if (cancelled) {
          await ctx.close().catch(() => {});
          return;
        }
        const copy = arrayBuffer.slice(0);
        const audioBuf = await ctx.decodeAudioData(copy);
        if (cancelled) {
          await ctx.close().catch(() => {});
          return;
        }
        const channel = audioBuf.getChannelData(0);
        const n = Math.max(8, barCount);
        const samplesPerBar = Math.max(1, Math.floor(channel.length / n));
        const out: number[] = [];
        for (let i = 0; i < n; i++) {
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, channel.length);
          let peak = 0;
          for (let j = start; j < end; j++) {
            const v = Math.abs(channel[j]!);
            if (v > peak) peak = v;
          }
          out.push(peak);
        }
        const max = Math.max(...out, 1e-6);
        if (!cancelled) setPeaks(out.map((v) => v / max));
        await ctx.close().catch(() => {});
      } catch {
        if (!cancelled) setUseFallback(true);
        await ctx.close().catch(() => {});
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [src, barCount]);

  if (useFallback || !peaks) {
    return <UGCMusicWaveform className={className} barClassName={barClassName} bars={barCount} />;
  }

  return (
    <div className={`flex h-full min-h-[24px] max-h-10 items-end justify-stretch gap-[1px] ${className}`} aria-hidden>
      {peaks.map((h, i) => (
        <div
          key={i}
          className={`min-w-[2px] flex-1 rounded-full opacity-90 ${barClassName}`}
          style={{ height: `${Math.max(10, Math.round(h * 100))}%` }}
        />
      ))}
    </div>
  );
};

export default UGCAudioWaveform;
